import { DynamicIndexObjectType, Networks } from '@iotv/iotv-v3-types'
import { GatewayProps, GatewayState, IGateway } from '../../ModelClassInterfaces'
import { Milliseconds } from '../../constants'
import { UserAttributeKeyDatePrecision } from '../../types'
import { ConstrainedUserAttDefs, UserAttributeDefs } from '../../types/ModelTypes'
import { toDatePickerFormat } from '../../util/time/millisecondOperations'
import { IsLocatableByPosition } from '../Interfaces'
import { IAWOLEnabled } from '../Interfaces/TimeRelated'
import { GeoModelCommon } from './GeoModelCommon'

const typeName = 'Gateway'

export enum BackhaulTypes {
  WiFi = 'WiFi',
  Ethernet = 'Ethernet',
  '4G' = '4G',
}

class Gateway extends GeoModelCommon implements IsLocatableByPosition, DynamicIndexObjectType, IAWOLEnabled, Partial<GatewayProps>, IGateway {
  constructor(ob: any) {
    super({ ...ob, type: ob.type ?? typeName })
  }

  getStateKeys(): string[] {
    let stateAttKeys: string[] = ['primaryGatewayUplinkCount', 'secondaryGatewayReceiveCount']
    return stateAttKeys
  }
  battery?: number
  lastSeen: number | undefined
  lastDeviceSeen?: string
  networkIP?: string
  networkKey?: Networks
  model?: string
  manufacturer?: string
  serialNumber?: string
  backhaul?: BackhaulTypes
  installDate?: number
  ports?: number
  channels?: number
  eui: string | undefined
  frequency?: number
  _permittedAWOL: number | undefined
  isAWOL?: boolean
  AWOLHistory?: number[]
  lastSendPeriod?: number
  primaryGatewayUplinkCount?: number
  secondaryGatewayReceiveCount?: number
  state: GatewayState | undefined
  temp: any
  recentDevices?: string[]

  congested?: boolean
  congesting?: boolean
  batteryLow?: number
  batteryVeryLow?: number
  batteryDead?: number

  //HW
  remoteEnabled?: boolean
  firmwareVersion?: string
  GWUserID?: string
  GWUserPW?: string

  //Backhaul
  WAPESSID?: string
  modemModel?: string
  modemSerialNumber?: string
  SIMNumber?: string
  SIMPUK?: string
  WAPDetails?: string
  WAPEncryption?: string
  WAPKey?: string
  WCEncryption?: string
  WCKey?: string

  //antennaAttributes
  antennaModel?: string
  antennaAble?: string
  cable?: boolean

  downlinkCapacity?: number
  uplinkCapcity?: number

  getUserAttributes(): ConstrainedUserAttDefs<IGateway> & UserAttributeDefs {
    return {
      ...super.getUserAttributes(),
      name: { type: 'String', required: true, editable: true },
      eui: {
        type: 'String',
        required: true,
        editable: false,
        label: 'Gateway EUI',
        name: 'eui',
        validation: {
          regex: '^(?:[0-9a-fA-F]{2}){4,}$',
          pass: true,
          invalidMessage: 'must be valid hexadecimal string with even number of characters and length 8 or more',
        },
        valueFn: (newValue: string | undefined) => (newValue ? newValue.toUpperCase() : undefined),
        displayFn: (newValue: string | undefined) => (newValue ? newValue : ''),
      },
      networkIP: { name: 'networkIP', type: 'String', editable: true, label: 'NW IP' },
      networkKey: { name: 'networkKey', type: 'String', editable: true, label: 'NW AS' },

      lastSeen: {
        type: 'Date',
        editable: false,
        label: 'Last Seen',
        name: 'lastSeen',
        precision: UserAttributeKeyDatePrecision.DateTimeSecs,
      },

      state: { type: 'Enum', editable: true, label: 'State', name: 'state', required: true, enumKv: GatewayState },
      lastDeviceSeen: { type: 'String', editable: false, label: 'lastDeviceSeen', name: 'lastDeviceSeen' },
      model: { type: 'String', editable: true, label: 'Model', name: 'model' },
      manufacturer: { type: 'String', editable: true, label: 'Manufacturer', name: 'manufacturer' },
      serialNumber: { type: 'String', editable: true, label: 'Serial Number', name: 'serialNumber' },
      backhaul: {
        type: 'Enum',
        editable: true,
        label: 'Backhaul Type',
        name: 'backhaul',
        enumKv: BackhaulTypes,
      },
      lastAWOL: {
        type: 'Number',
        editable: false,
        label: 'Last AWOL',
        name: 'Last AWOL',
        displayFn: () => this.AWOLHistory && this.AWOLHistory[this.AWOLHistory.length - 1],
        valueFn: () => this.AWOLHistory && this.AWOLHistory[this.AWOLHistory.length - 1],
      },
      AWOLHistory: {
        type: 'Number',
        editable: false,
        label: 'AWOL History',
        name: 'AWOL History',
        displayFn: () => this.AWOLHistory && [...this.AWOLHistory].reverse().join(', '),
        valueFn: () => this.AWOLHistory && [...this.AWOLHistory].reverse().join(', '),
      },
      installDate: {
        type: 'Date',
        editable: true,
        label: 'Install Date',
        name: 'installDate',
        displayFn: (actualValue: number | undefined) => (actualValue ? toDatePickerFormat(actualValue) : undefined),
        valueFn: (newValue: number | undefined) => (newValue ? new Date(newValue).valueOf() : undefined),
      },
      ports: { type: 'Number', editable: true, label: 'Ports', name: 'ports' },
      lastSendPeriod: { type: 'Number', editable: false, label: 'Last Send Period', name: 'Last Send Period' },
      primaryGatewayUplinkCount: { type: 'Number', editable: false, label: 'Primary Receives', name: 'primaryGatewayUplinkCount' },
      secondaryGatewayReceiveCount: { type: 'Number', editable: false, label: 'Secondary Receives', name: 'secondaryGatewayReceiveCount' },
      // primaryDevices: { type: 'Number', editable: false, label: 'Primary Devices', valueFn()},
      channels: { type: 'Number', editable: true, label: 'Channels', name: 'Channels' },
      frequency: { type: 'Number', editable: true, label: 'Frequency', name: 'frequency' },
      isAWOL: { type: 'Boolean', editable: false, label: 'Is AWOL?', name: 'isAWOL' },
      _permittedAWOL: {
        type: 'Number',
        editable: true,
        label: 'Max AWOL (mins)',
        name: '_permittedAWOL',
        displayFn: (actualValue: number | undefined) => (actualValue ? actualValue / Milliseconds.MINUTE : undefined),
        valueFn: (newValue: number | undefined) => (newValue ? newValue * Milliseconds.MINUTE : undefined),
      },

      remoteEnabled: { type: 'Boolean', editable: true, label: 'Remote Enabled', name: 'Remote Enabled' },
      firmwareVersion: { type: 'String', editable: true, label: 'Firmware Version', name: 'Firmware Version' },
      GWUserID: { type: 'String', editable: true, label: 'GW User ID', name: 'GW User ID' },
      GWUserPW: { type: 'String', editable: true, label: 'GW Password', name: 'GW Password' },
      LastUpdate: { type: 'String', editable: true, label: 'Last Updated', name: 'LastUpdate' },
      WAPESSID: { type: 'String', editable: true, label: 'WAP ESSID', name: 'WAP ESSID' },
      modemModel: { type: 'String', editable: true, label: 'Modem Model', name: 'Modem Model' },
      modemSerialNumber: { type: 'String', editable: true, label: 'Modem Serial Number', name: 'Modem Serial Number' },
      SIMNumber: { type: 'String', editable: true, label: 'SIM Number', name: 'SIM Number' },
      SIMPUK: { type: 'String', editable: true, label: 'SIM PUK', name: 'SIM PUK' },
    }
  }

  set permittedAWOL_MS(awolMs: number | undefined) {
    this._permittedAWOL = awolMs
  }

  get permittedAWOL_MS() {
    return this._permittedAWOL
  }
}

export { Gateway }
